'use client';
import React, { useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import PageHero from '@/components/ui/PageHero';

interface Feature {
  icon: string;
  label: string;
}

interface Product {
  id: string;
  icon: string;
  title: string;
  description: string;
  features: Feature[];
  color: string;
  bgColor: string;
  href: string;
}

const products: Product[] = [
  {
    id: 'fleet',
    icon: 'TruckIcon',
    title: 'Ride Hailing Platform',
    description:
      'A comprehensive SaaS platform for businesses operating vehicle fleets of any size. Get complete visibility, control, and intelligence over every asset — from 5 vehicles to 500+.',
    features: [
      { icon: 'MapPinIcon', label: 'Live GPS Tracking with 5-second updates' },
      { icon: 'WrenchScrewdriverIcon', label: 'AI-powered Predictive Maintenance alerts' },
      { icon: 'UserGroupIcon', label: 'Driver Behavior Scoring & Management' },
      { icon: 'ChartBarIcon', label: 'Fleet Analytics — cost & efficiency reports' },
      { icon: 'BellAlertIcon', label: 'Geofence Alerts & zone-based triggers' },
      { icon: 'DocumentChartBarIcon', label: 'Audit-ready Compliance Reports' },
    ],
    color: '#0066FF',
    bgColor: 'rgba(0,102,255,0.08)',
    href: '/products/ride-hailing',
  },
  {
    id: 'logistics',
    icon: 'ArrowPathIcon',
    title: 'Logistics Platform',
    description:
      'End-to-end logistics intelligence with AI-powered route planning, multi-stop optimization, warehouse management, and real-time supply chain visibility for B2B and B2C operations.',
    features: [
      { icon: 'MapIcon', label: 'AI-powered Multi-stop Route Optimization' },
      { icon: 'CubeIcon', label: 'Load Balancing & Capacity Optimization' },
      { icon: 'ClockIcon', label: 'ML-powered ETA Prediction' },
      { icon: 'ChartPieIcon', label: 'Demand Forecasting via historical analysis' },
      { icon: 'BuildingStorefrontIcon', label: 'Warehouse Management System Integration' },
      { icon: 'CurrencyRupeeIcon', label: 'Per-delivery Cost Analytics' },
    ],
    color: '#075DDF',
    bgColor: 'rgba(7,93,223,0.08)',
    href: '/products/logistics',
  },
  {
    id: 'courier',
    icon: 'BoltIcon',
    title: 'Delivery Services',
    description:
      'Hyperlocal delivery engine for instant bookings, intelligent driver matching, and real-time order tracking.',
    features: [
      { icon: 'DevicePhoneMobileIcon', label: 'Customer App for iOS & Android' },
      { icon: 'UserIcon', label: 'Driver App with earnings & navigation' },
      { icon: 'BuildingStorefrontIcon', label: 'Merchant Portal for store management' },
      { icon: 'ChatBubbleLeftRightIcon', label: 'WhatsApp Conversational Order Booking' },
      { icon: 'CreditCardIcon', label: 'Payment Gateway — UPI, card & COD' },
      { icon: 'StarIcon', label: 'Ratings & Reviews feedback loop' },
    ],
    color: '#075DDF',
    bgColor: 'rgba(26,111,181,0.08)',
    href: '/products/courier',
  },
  {
    id: 'facility-pro',
    icon: 'HomeModernIcon',
    title: 'Facility Pro',
    description:
      'Complete facility management platform covering plumbing, electrical, cleaning, HVAC, security, and more — with worker tracking, attendance, work order management, invoicing, and multi-branch control.',
    features: [
      { icon: 'DevicePhoneMobileIcon', label: 'Customer & Worker Apps (iOS & Android)' },
      { icon: 'CalendarDaysIcon', label: 'Smart Scheduling & Slot Management' },
      { icon: 'UserGroupIcon', label: 'Worker Onboarding & Verification' },
      { icon: 'MapPinIcon', label: 'Worker Live Tracking via App' },
      { icon: 'DocumentTextIcon', label: 'Work Order & Invoicing Management' },
      { icon: 'BuildingStorefrontIcon', label: 'Multi-branch Setup & Control' },
    ],
    color: '#0E7490',
    bgColor: 'rgba(14,116,144,0.08)',
    href: '/products/facility-pro',
  },
  {
    id: 'ai-call-center',
    icon: 'PhoneIcon',
    title: 'AI Based Call Center',
    description:
      'Intelligent AI-powered call center solution that automates customer support, booking confirmations, and driver communications — reducing operational costs while improving response times.',
    features: [
      { icon: 'MicrophoneIcon', label: 'AI Voice Bot for Inbound & Outbound Calls' },
      { icon: 'ChatBubbleLeftRightIcon', label: 'Multilingual Support — Tamil, Hindi, English' },
      { icon: 'BoltIcon', label: 'Instant Booking Confirmations via Call' },
      { icon: 'ClockIcon', label: '24/7 Automated Customer Support' },
      { icon: 'ChartBarIcon', label: 'Call Analytics & Sentiment Tracking' },
      { icon: 'ArrowPathIcon', label: 'CRM Integration & Escalation Routing' },
    ],
    color: '#0066FF',
    bgColor: 'rgba(0,102,255,0.08)',
    href: '/products/ai-call-center',
  },
  {
    id: 'ecommerce',
    icon: 'ShoppingBagIcon',
    title: 'E-Commerce & Retail',
    description:
      'A unified suite of e-commerce and delivery platforms — Food Delivery, Grocery, Multi-Vendor Marketplace, Single Store, and Delivery Integrations — all under one reliable, scalable umbrella.',
    features: [
      { icon: 'FireIcon', label: 'Food Delivery — Restaurant to doorstep' },
      { icon: 'ShoppingCartIcon', label: 'Grocery Delivery — Slot-based fulfilment' },
      { icon: 'Squares2X2Icon', label: 'Multi-Vendor Marketplace — Commission engine' },
      { icon: 'BuildingStorefrontIcon', label: 'Single Store — Instant digital commerce' },
      { icon: 'ArrowsRightLeftIcon', label: 'Delivery Integrations — Third-party APIs' },
      { icon: 'ShieldCheckIcon', label: 'Unified Analytics across all verticals' },
    ],
    color: '#0E7490',
    bgColor: 'rgba(14,116,144,0.08)',
    href: '/products/ecommerce',
  },
  {
    id: 'ellan-cpaas',
    icon: 'ChatBubbleLeftRightIcon',
    title: 'Ellan CPaaS',
    description:
      'A unified communication platform for SMS, WhatsApp Business API, RCS, and visual chatbot flows — with DLT compliance, campaign management, and real-time delivery analytics.',
    features: [
      { icon: 'ChatBubbleLeftRightIcon', label: 'Bulk SMS — DLT-compliant Sender IDs' },
      { icon: 'DevicePhoneMobileIcon', label: 'WhatsApp Business API — Broadcast & Inbox' },
      { icon: 'CpuChipIcon', label: 'No-code Visual Bot & Flow Builder' },
      { icon: 'SparklesIcon', label: 'RCS Rich Messaging with Quick Replies' },
      { icon: 'UserGroupIcon', label: 'Unified Contacts & Campaign Manager' },
      { icon: 'ChartBarIcon', label: 'Real-time Delivery Reports & Webhooks' },
    ],
    color: '#6D28D9',
    bgColor: 'rgba(109,40,217,0.08)',
    href: '/products/ellan-cpaas',
  },
];

export default function ProductsContent() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="pt-24">
      {/* Hero */}
      <PageHero className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-on-scroll stagger-1">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/15 text-[11px] font-semibold tracking-widest text-white uppercase mb-6">
              Our Products
            </span>
            <h1 className="font-display text-5xl md:text-7xl font-semibold text-white tracking-tight leading-tight mb-6 mt-2">
              Seven platforms,{' '}
              <span className="italic font-light text-white/80">one vision.</span>
            </h1>
            <p className="text-white/70 text-xl max-w-2xl mx-auto leading-relaxed animate-on-scroll stagger-2">
              Everything you need to build, launch, and scale a mobility or logistics business under your brand.
            </p>
          </div>
        </div>
      </PageHero>

      {/* Our Products Grid */}
      <div className="bg-[#FFFFFF] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Title */}
          <div className="text-center mb-16 animate-on-scroll stagger-1">
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight mb-4">
              Our Products
            </h2>
            <p className="text-[#475569] text-lg max-w-xl mx-auto">
              Purpose-built platforms for every stage of your mobility and logistics journey.
            </p>
          </div>

          {/* Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, idx) => (
              <div
                key={product.id}
                className={`animate-on-scroll stagger-${idx + 1} bg-white rounded-2xl border border-[rgba(15,23,42,0.07)] shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col overflow-hidden`}
              >
                {/* Card Header */}
                <div
                  className="px-8 pt-8 pb-6"
                  style={{ background: product.bgColor }}
                >
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                    style={{ background: product.color }}
                  >
                    <Icon
                      name={product.icon as Parameters<typeof Icon>[0]['name']}
                      size={28}
                      className="text-white"
                    />
                  </div>
                  <h3 className="font-display text-2xl font-semibold text-[#0F172A] leading-tight mb-3">
                    {product.title}
                  </h3>
                  <p className="text-[#475569] text-sm leading-relaxed">
                    {product.description}
                  </p>
                </div>

                {/* Features */}
                <div className="px-8 py-6 flex-1">
                  <p
                    className="text-xs font-semibold uppercase tracking-widest mb-4"
                    style={{ color: product.color }}
                  >
                    Key Features
                  </p>
                  <ul className="flex flex-col gap-3">
                    {product.features.map((feature) => (
                      <li key={feature.label} className="flex items-start gap-3">
                        <div
                          className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ background: product.bgColor }}
                        >
                          <Icon
                            name={feature.icon as Parameters<typeof Icon>[0]['name']}
                            size={13}
                            style={{ color: product.color }}
                          />
                        </div>
                        <span className="text-sm text-[#475569] leading-snug">{feature.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className="px-8 pb-8 flex flex-row gap-2">
                  <Link
                    href={product.href}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-all duration-200 hover:opacity-80 active:scale-[0.98]"
                    style={{ borderColor: product.color, color: product.color }}
                  >
                    <Icon name="InformationCircleIcon" size={13} />
                    Details
                  </Link>
                  <Link
                    href="/demo-booking"
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
                    style={{ background: `linear-gradient(135deg, ${product.color}, #0321A8)` }}
                  >
                    Book Demo
                    <Icon name="ArrowRightIcon" size={13} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="py-20 bg-white text-center">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="animate-on-scroll stagger-1">
            <h2 className="font-display text-4xl font-semibold text-[#0F172A] mb-4">
              Not sure which platform fits?
            </h2>
            <p className="text-[#475569] text-lg mb-8">
              Talk to our team and we'll design a custom stack for your business model.
            </p>
            <Link
              href="/demo-booking"
              className="btn-primary inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold"
            >
              Book a Demo
              <Icon name="ArrowRightIcon" size={18} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}